import type { LoadableLibrary } from '@loadable/component';
import loadable from '@loadable/component';

import type { ISO2 } from '@/constants/countries';

/**
 * This component is being used in both deprecated-web and news site where svg loading
 * leverages different types of loaders, which is why we need to handle 3 different formats.
 */
type ImageModuleType =
  | string
  | { default: string }
  | { default: { src: string } };

/**
 * Converts multiple module formats into string URL.
 *
 * Url loader - small images - base64 encoded
 * File loader - larger images url pointing to the root directory with hash as name
 * Next image loader - processed svg with url pointing to the root directory with hash as a name
 */
const getImageUrl = (module: ImageModuleType) => {
  // URL or DataUrl
  if (typeof module === 'string') return module;

  const defaultValue = module?.default;
  if (!defaultValue) return null;

  // ES Module
  if (typeof defaultValue === 'string') return defaultValue;

  // Next image loader
  if (typeof defaultValue.src === 'string') return defaultValue.src;

  return null;
};

const COUNTRY_IMAGE_MAP: Partial<
  Record<ISO2, LoadableLibrary<ImageModuleType>>
> = {
  AE: loadable.lib(() => import('@assets/images/markets/ae.svg?url')),
  AR: loadable.lib(() => import('@assets/images/markets/ar.svg?url')),
  AT: loadable.lib(() => import('@assets/images/markets/at.svg?url')),
  AU: loadable.lib(() => import('@assets/images/markets/au.svg?url')),
  BD: loadable.lib(() => import('@assets/images/markets/bd.svg?url')),
  BE: loadable.lib(() => import('@assets/images/markets/be.svg?url')),
  BG: loadable.lib(() => import('@assets/images/markets/bg.svg?url')),
  BH: loadable.lib(() => import('@assets/images/markets/bh.svg?url')),
  BM: loadable.lib(() => import('@assets/images/markets/bm.svg?url')),
  BR: loadable.lib(() => import('@assets/images/markets/br.svg?url')),
  BW: loadable.lib(() => import('@assets/images/markets/bw.svg?url')),
  CA: loadable.lib(() => import('@assets/images/markets/ca.svg?url')),
  CH: loadable.lib(() => import('@assets/images/markets/ch.svg?url')),
  CI: loadable.lib(() => import('@assets/images/markets/ci.svg?url')),
  CL: loadable.lib(() => import('@assets/images/markets/cl.svg?url')),
  CN: loadable.lib(() => import('@assets/images/markets/cn.svg?url')),
  CO: loadable.lib(() => import('@assets/images/markets/co.svg?url')),
  CY: loadable.lib(() => import('@assets/images/markets/cy.svg?url')),
  CZ: loadable.lib(() => import('@assets/images/markets/cz.svg?url')),
  DE: loadable.lib(() => import('@assets/images/markets/de.svg?url')),
  DK: loadable.lib(() => import('@assets/images/markets/dk.svg?url')),
  EE: loadable.lib(() => import('@assets/images/markets/ee.svg?url')),
  EG: loadable.lib(() => import('@assets/images/markets/eg.svg?url')),
  ES: loadable.lib(() => import('@assets/images/markets/es.svg?url')),
  FI: loadable.lib(() => import('@assets/images/markets/fi.svg?url')),
  FR: loadable.lib(() => import('@assets/images/markets/fr.svg?url')),
  GB: loadable.lib(() => import('@assets/images/markets/gb.svg?url')),
  GH: loadable.lib(() => import('@assets/images/markets/gh.svg?url')),
  GR: loadable.lib(() => import('@assets/images/markets/gr.svg?url')),
  HK: loadable.lib(() => import('@assets/images/markets/hk.svg?url')),
  HR: loadable.lib(() => import('@assets/images/markets/hr.svg?url')),
  HU: loadable.lib(() => import('@assets/images/markets/hu.svg?url')),
  ID: loadable.lib(() => import('@assets/images/markets/id.svg?url')),
  IE: loadable.lib(() => import('@assets/images/markets/ie.svg?url')),
  IL: loadable.lib(() => import('@assets/images/markets/il.svg?url')),
  IN: loadable.lib(() => import('@assets/images/markets/in.svg?url')),
  IS: loadable.lib(() => import('@assets/images/markets/is.svg?url')),
  IT: loadable.lib(() => import('@assets/images/markets/it.svg?url')),
  JM: loadable.lib(() => import('@assets/images/markets/jm.svg?url')),
  JO: loadable.lib(() => import('@assets/images/markets/jo.svg?url')),
  JP: loadable.lib(() => import('@assets/images/markets/jp.svg?url')),
  KE: loadable.lib(() => import('@assets/images/markets/ke.svg?url')),
  KR: loadable.lib(() => import('@assets/images/markets/kr.svg?url')),
  KW: loadable.lib(() => import('@assets/images/markets/kw.svg?url')),
  KZ: loadable.lib(() => import('@assets/images/markets/kz.svg?url')),
  LB: loadable.lib(() => import('@assets/images/markets/lb.svg?url')),
  LK: loadable.lib(() => import('@assets/images/markets/lk.svg?url')),
  LT: loadable.lib(() => import('@assets/images/markets/lt.svg?url')),
  LU: loadable.lib(() => import('@assets/images/markets/lu.svg?url')),
  LV: loadable.lib(() => import('@assets/images/markets/lv.svg?url')),
  MA: loadable.lib(() => import('@assets/images/markets/ma.svg?url')),
  MT: loadable.lib(() => import('@assets/images/markets/mt.svg?url')),
  MU: loadable.lib(() => import('@assets/images/markets/mu.svg?url')),
  MW: loadable.lib(() => import('@assets/images/markets/mw.svg?url')),
  MX: loadable.lib(() => import('@assets/images/markets/mx.svg?url')),
  MY: loadable.lib(() => import('@assets/images/markets/my.svg?url')),
  NA: loadable.lib(() => import('@assets/images/markets/na.svg?url')),
  NG: loadable.lib(() => import('@assets/images/markets/ng.svg?url')),
  NL: loadable.lib(() => import('@assets/images/markets/nl.svg?url')),
  NO: loadable.lib(() => import('@assets/images/markets/no.svg?url')),
  NZ: loadable.lib(() => import('@assets/images/markets/nz.svg?url')),
  OM: loadable.lib(() => import('@assets/images/markets/om.svg?url')),
  PE: loadable.lib(() => import('@assets/images/markets/pe.svg?url')),
  PH: loadable.lib(() => import('@assets/images/markets/ph.svg?url')),
  PK: loadable.lib(() => import('@assets/images/markets/pk.svg?url')),
  PL: loadable.lib(() => import('@assets/images/markets/pl.svg?url')),
  PS: loadable.lib(() => import('@assets/images/markets/ps.svg?url')),
  PT: loadable.lib(() => import('@assets/images/markets/pt.svg?url')),
  QA: loadable.lib(() => import('@assets/images/markets/qa.svg?url')),
  RO: loadable.lib(() => import('@assets/images/markets/ro.svg?url')),
  RS: loadable.lib(() => import('@assets/images/markets/rs.svg?url')),
  RU: loadable.lib(() => import('@assets/images/markets/ru.svg?url')),
  SA: loadable.lib(() => import('@assets/images/markets/sa.svg?url')),
  SE: loadable.lib(() => import('@assets/images/markets/se.svg?url')),
  SG: loadable.lib(() => import('@assets/images/markets/sg.svg?url')),
  SI: loadable.lib(() => import('@assets/images/markets/si.svg?url')),
  SK: loadable.lib(() => import('@assets/images/markets/sk.svg?url')),
  TH: loadable.lib(() => import('@assets/images/markets/th.svg?url')),
  TN: loadable.lib(() => import('@assets/images/markets/tn.svg?url')),
  TR: loadable.lib(() => import('@assets/images/markets/tr.svg?url')),
  TT: loadable.lib(() => import('@assets/images/markets/tt.svg?url')),
  TW: loadable.lib(() => import('@assets/images/markets/tw.svg?url')),
  TZ: loadable.lib(() => import('@assets/images/markets/tz.svg?url')),
  UA: loadable.lib(() => import('@assets/images/markets/ua.svg?url')),
  UG: loadable.lib(() => import('@assets/images/markets/ug.svg?url')),
  US: loadable.lib(() => import('@assets/images/markets/us.svg?url')),
  VE: loadable.lib(() => import('@assets/images/markets/ve.svg?url')),
  VN: loadable.lib(() => import('@assets/images/markets/vn.svg?url')),
  ZA: loadable.lib(() => import('@assets/images/markets/za.svg?url')),
  ZM: loadable.lib(() => import('@assets/images/markets/zm.svg?url')),
  ZW: loadable.lib(() => import('@assets/images/markets/zw.svg?url')),
};

type Props = {
  countryISO: ISO2 | null;
};

export const CountryFlag = ({ countryISO }: Props) => {
  const IconLoader =
    countryISO && countryISO in COUNTRY_IMAGE_MAP
      ? COUNTRY_IMAGE_MAP[countryISO]
      : null;

  if (!IconLoader) return null;
  return (
    <IconLoader>
      {(iconUrlOrModule) => {
        const url = getImageUrl(iconUrlOrModule);

        if (!url) return null;
        return (
          <img
            src={url}
            alt={`${countryISO ?? ''} flag`}
            height={11}
            width={16}
            className="block h-auto w-full"
          />
        );
      }}
    </IconLoader>
  );
};
